<template>
<div>
  <div :class="['plan-detail', { 'screen-lg': onTabletUp }]">
    <div class="detail-hero" :style="`backgroundImage: url(${platelist.imageUrl ? platelist.imageUrl : ''}`">
      <div class="date-select-wrapper">
        <div v-if="numberOfWeeks > 1" class="date-select">
          <span @click="prevWeek">
            <BaseIcon class="date-selec-icon" name="chevron-left" />
          </span>
          <h4>Week {{ currentWeekOffset }}</h4>
          <span @click="nextWeek">
            <BaseIcon class="date-selec-icon" name="chevron-right" />
          </span>
        </div>
      </div>

      <div class="tag-wrapper">
        <div class="tag" v-if="hasDays"> <img class="icon-cloche" src="@assets/images/icon-cloche.png" /> {{ numberOfDishes }} dishes </div>
        <div class="tag" v-if="hasDays"> <img class="icon-calendar" src="@assets/images/icon-calendar.png" /> {{ days.length }} days </div>
      </div>
    </div>

    <div class="detail-content-container">
      <div class="detail-header" v-if="hasDays">
        <h1>{{ platelist.landingpagetitle }}</h1>
        <p>{{ platelist.landingpagedescription }}</p>
      </div>
      <div class="detail-section" v-for="(day, i) in days" :key="day.id" v-if="hasDays">
        <h3 class="section-title" v-if="dayHasDishes(day)">
          {{ day.headerValue }}
          <small>
            Calories {{ formattedMacro(day, 'calories').unit }} • Carbs {{ formattedMacro(day, 'carbs').unit }} • Protein
            {{ formattedMacro(day, 'protein').unit }} • Fat {{ formattedMacro(day, 'fat').unit }}
          </small>
        </h3>

        <div v-for="dish in dishes(day)" v-if="dayHasDishes(day)">
          <div class="mealtime-section" v-if="dish[0].mealtime === 'Breakfast'">
            <h4>{{ $t('lblMorning') }}</h4>
            <MealCardHorizontal v-for="(dishData, i) in dish[1]" :key="i" :dish="dishData" />
          </div>

          <div class="mealtime-section" v-if="dish[0].mealtime === 'Lunch'">
            <h4>{{ $t('lblAfternoon') }}</h4>
            <MealCardHorizontal v-for="(dishData, i) in dish[1]" :key="i" :dish="dishData" />
          </div>

          <div class="mealtime-section" v-if="dish[0].mealtime === 'Dinner'">
            <h4>{{ $t('lblEvening') }}</h4>
            <MealCardHorizontal v-for="(dishData, i) in dish[1]" :key="i" :dish="dishData" />
          </div>
        </div>
      </div>

      <div class="notice-ended" v-if="!hasDays">
        <h2>{{ days.title }}</h2>
        <p>{{ days.body }}</p>
        <BaseButton type="button" @click="toPlans">
          {{ days.ctaText }}
        </BaseButton>
      </div>
    </div>
  </div>

  <transition name="fade">
    <BaseButton class="btn-start-plan" type="button" @click="toLogin" v-if="hasDays && !drawerTransitioning && isOpen">
      Sign-in & Start Plan
    </BaseButton>
  </transition>
</div>
</template>

<script>
import store from '@state/store';
import MealCardHorizontal from '../MealCard/MealCardHorizontal';
import { macrosMixin } from 'macrosMixin';
import utils from '@src/utils/utils';
import {
  kitchenDrawerComputed,
  kitchenDrawerMethods,
  mainComputed,
  mainMethods,
  planComputed,
  planMethods,
  platelistComputed,
  platelistMethods,
  primaryNavComputed,
  primaryNavMethods,
} from '@state/helpers';

export default {
  mixins: [macrosMixin],
  components: {
    MealCardHorizontal,
  },
  props: {
    isEmbed: {
      type: Boolean,
    },
    data: {
      type: Object,
    },
    onTabletUp: {
      type: Boolean,
    },
  },
  data() {
    return {
      numberOfWeeks: 0,
      numberOfDishes: 0,
      currentWeekOffset: 1,
    };
  },
  computed: {
    ...mainComputed,
    ...platelistComputed,
    ...planComputed,
    ...kitchenDrawerComputed,
    ...primaryNavComputed,
    currentPrimaryView() {
      return this.primaryNavContent[this.activePrimaryNavIndex];
    },
    platelist() {
      return this.data;
    },
    days() {
      if (this.platelistDays[this.platelist.id].length > 0) {
        const daysSubset = this.platelistDays[this.platelist.id].filter((day) => {
          return day.dateOffset >= 7 * (this.currentWeekOffset - 1) && day.dateOffset < 7 * this.currentWeekOffset;
        });
        return daysSubset;
      } else {
        return this.platelistDays[this.platelist.id];
      }
    },
    hasDays() {
      return this.days.length > 0 ? true : false;
    },
  },
  mounted() {
    const platelistDetailDays = this.platelistDays[this.platelist.id];

    if (platelistDetailDays.length > 0) {
      this.numberOfWeeks = Math.ceil((platelistDetailDays[platelistDetailDays.length - 1].dateOffset + 1) / 7);
      this.numberOfDishes = 0;

      this.days.forEach((day) => {
        this.numberOfDishes += day.meals.length + day.recipes.length;
      });
    }
  },
  methods: {
    ...kitchenDrawerMethods,
    ...mainMethods,
    ...planMethods,
    ...platelistMethods,
    ...primaryNavMethods,
    formatDishes(day) {
      return day.meals
        .map((meal) => {
          return { dishType: 'm', ...meal };
        })
        .concat(
          day.recipes.map((recipe) => {
            return { dishType: 'r', ...recipe };
          })
        )
        .concat(
          day.leftovers.map((leftover) => {
            return { dishType: 'r', mealTime: leftover.mealTime, ...leftover.recipe };
          })
        );
    },
    dayHasDishes(day) {
      return day.meals.concat(day.recipes).length ? true : false;
    },
    dishes(day) {
      const dishes = [];
      const concatDishes = this.formatDishes(day);
      const breakfastDishes = concatDishes.filter((dish) => {
        if (dish.mealTime) {
          return dish.mealTime.name === 'Breakfast';
        }
      });
      const lunchDishes = concatDishes.filter((dish) => {
        if (dish.mealTime) {
          return dish.mealTime.name === 'Lunch';
        }
      });
      const dinnerDishes = concatDishes.filter((dish) => {
        if (dish.mealTime) {
          return dish.mealTime.name === 'Dinner';
        }
      });

      if (breakfastDishes.length) {
        dishes.push([{ mealtime: 'Breakfast' }, breakfastDishes]);
      }

      if (lunchDishes.length) {
        dishes.push([{ mealtime: 'Lunch' }, lunchDishes]);
      }

      if (dinnerDishes.length) {
        dishes.push([{ mealtime: 'Dinner' }, dinnerDishes]);
      }

      return dishes;
    },
    toPlans() {
      this.navigateHigher();
    },
    toLogin() {
      this.$router.replace({ name: 'login' });
    },
    prevWeek() {
      if (this.currentWeekOffset > 0) {
        this.currentWeekOffset -= 1;
      }
    },
    nextWeek() {
      if (this.currentWeekOffset < this.numberOfWeeks) {
        this.currentWeekOffset += 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.plan-detail {
  @extend %scrollable-drawer-content;

  background-color: $color-plan-detail-bg;

  .detail-hero {
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    flex-direction: column;
    height: 23.7rem;
    justify-content: space-between;
    margin-bottom: 1.6rem;
    padding: 0.8rem;

    .date-select-wrapper {
      padding: 1rem 0 0;

      .date-select {
        align-items: center;
        background-color: $color-plan-detail-hero-date-select-bg;
        border: 1px solid $base-border-color;
        border-radius: $base-border-radius;
        display: flex;
        font-size: 1.3rem;
        justify-content: space-between;
        margin: 0 auto;
        padding: 1rem 2rem;
        width: 16rem;

        h4 {
          font-size: 1.4rem;
          margin-bottom: 0;
        }

        .date-selec-icon {
          color: $color-action;
          font-size: 1.5rem;

          &:hover {
            cursor: pointer;
          }
        }
      }
    }

    .tag-wrapper {
      display: flex;
      justify-content: space-between;
    }

    .tag {
      align-items: center;
      background-color: $color-meal-card-horizontal-tag-bg;
      border-radius: $base-border-radius - 0.4;
      color: $color-meal-card-horizontal-tag-text;
      display: flex;
      font-size: 1.3rem;
      justify-content: center;
      padding: 0.6rem 1rem;

      .icon-cloche {
        height: 1.68rem;
        margin-right: 0.8rem;
        width: 2rem;
      }

      .icon-calendar {
        height: 1.7rem;
        margin-right: 0.8rem;
        width: 1.6rem;
      }
    }
  }

  .detail-content-container {
    padding: 0 1.6rem 23rem;

    .detail-header {
      margin-bottom: 2rem;

      h1 {
        font-size: 2.4rem;
        line-height: 1;
      }
    }

    .detail-section {
      margin-bottom: 3.2rem;

      &:last-of-type {
        margin-bottom: 6.2rem;
      }
    }

    .section-title {
      color: $color-meal-card-horizontal-title;
      font-size: 1.8rem;
      line-height: 2.175rem;
      margin-bottom: 1rem;

      small {
        color: $color-meal-card-horizontal-subtitle;
        display: block;
        font-size: 75%;
      }
    }

    .mealtime-section {
      margin-bottom: 2rem;
    }

    .notice-ended {
      align-items: center;
      display: flex;
      flex-direction: column;

      h2,
      p {
        margin-bottom: 1.4rem;
      }

      p {
        font-size: 1.4rem;
        line-height: 1.3;
        text-align: center;
      }
    }

    .fade-enter-active {
      transition: opacity 0.2s;
    }
    .fade-leave-active {
      transition: opacity 0.2s;
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
      opacity: 0;
    }
  }

  &.screen-lg {
    border-bottom-left-radius: 2.5rem;
    border-bottom-right-radius: 2.5rem;
    box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.3);

    .detail-content-container {
      padding-bottom: 0;
    }
  }
}

.btn-start-plan {
  background-color: $color-plan-detail-cta-button-bg;
  border-radius: $size-plan-detail-apply-btn-border-radius;
  bottom: 3rem;
  box-shadow: $color-plan-detail-cta-button-shadow;
  color: $color-plan-detail-cta-button-text;
  left: calc(50vw - 8rem);
  padding: 1rem 1.5rem;
  position: fixed;
  width: 16rem;

  // iPad Pro
  @include media('xxl', 'ipad-pro') {
    bottom: 12rem;
  }
}
</style>
